import { useEffect, useMemo, useState } from 'react';

import FormModal from '../../../Modals/FormModal';
import InputField from '../../../Forms/Input/InputField';
import ActionButton from '../../../Buttons/ActionButton';

import useProductCatalog from '../../../../hooks/productpanel/useProductCatalog';
import useDebounced from '../../../../hooks/useDebounced';

import CategoryButtonSelectable from '../../ProductBrowser/Category/CategoryButtonSelectable';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  branchId: number;
  categoryId: number | null;
  selectedSubCategoryId: number | null;
  onSelect: (subCategoryId: number | null) => void;
}

const POSSubCategoryFilterModal = ({
  isOpen,
  onClose,
  branchId,
  categoryId,
  selectedSubCategoryId,
  onSelect,
}: Props) => {
  const { subCategories, fetchSubCategories } = useProductCatalog(branchId);

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounced(search.trim().toLowerCase(), 300);

  const [localSubCategoryId, setLocalSubCategoryId] = useState<number | null>(
    selectedSubCategoryId
  );

  // =====================================================
  // 🔹 Load
  // =====================================================

  useEffect(() => {
    if (isOpen && branchId && categoryId) {
      fetchSubCategories(categoryId);
    }
  }, [isOpen, branchId, categoryId, fetchSubCategories]);

  // =====================================================
  // 🔹 Sync
  // =====================================================

  useEffect(() => {
    setLocalSubCategoryId(selectedSubCategoryId);
  }, [selectedSubCategoryId]);

  // =====================================================
  // 🔹 Reset Search
  // =====================================================

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
    }
  }, [isOpen]);

  // =====================================================
  // 🔹 Filtered
  // =====================================================

  const filteredSubCategories = useMemo(() => {
    if (!debouncedSearch) {
      return subCategories;
    }

    return subCategories.filter((subCategory) =>
      subCategory.name.toLowerCase().includes(debouncedSearch)
    );
  }, [subCategories, debouncedSearch]);

  // =====================================================
  // 🔹 Confirm
  // =====================================================

  const handleConfirm = () => {
    onSelect(localSubCategoryId);

    onClose();
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Seleccionar subcategoría"
      maxWidth="max-w-3xl"
    >
      {!categoryId ? (
        <div
          className="
            rounded-xl
            border
            border-stroke
            p-5
            text-center
            text-sm
            text-body
          "
        >
          Primero selecciona una categoría.
        </div>
      ) : (
        <>
          {/* ===================================================== */}
          {/* 🔹 Search */}
          {/* ===================================================== */}

          <InputField
            label="Buscar subcategoría"
            name="search"
            type="text"
            value={search}
            placeholder="Buscar subcategoría..."
            iconKey="search"
            onChange={(e) => setSearch(e.target.value)}
          />

          {/* ===================================================== */}
          {/* 🔹 List */}
          {/* ===================================================== */}

          <div
            className="
              mt-5
              max-h-[350px]
              overflow-y-auto
              rounded-xl
              border
              border-stroke
              bg-slate-50
              p-4
              dark:border-strokedark
              dark:bg-boxdark
            "
          >
            <div
              className="
                grid
                grid-cols-1
                gap-3
                sm:grid-cols-2
              "
            >
              <CategoryButtonSelectable
                id={0}
                name="Todas las subcategorías"
                isSelected={localSubCategoryId === null}
                onClick={() => setLocalSubCategoryId(null)}
              />

              {filteredSubCategories.map((subCategory) => (
                <CategoryButtonSelectable
                  key={subCategory.id}
                  id={subCategory.id}
                  name={subCategory.name}
                  isSelected={localSubCategoryId === subCategory.id}
                  onClick={() => setLocalSubCategoryId(subCategory.id)}
                />
              ))}
            </div>

            {filteredSubCategories.length === 0 && (
              <div
                className="
                  py-10
                  text-center
                  text-sm
                  text-body
                "
              >
                No se encontraron subcategorías.
              </div>
            )}
          </div>

          {/* ===================================================== */}
          {/* 🔹 Actions */}
          {/* ===================================================== */}

          <div
            className="
              mt-6
              flex
              gap-2
            "
          >
            <ActionButton
              label="Cancelar"
              variant="secondary"
              onClick={onClose}
            />

            <ActionButton
              label="Seleccionar"
              variant="primary"
              onClick={handleConfirm}
            />
          </div>
        </>
      )}
    </FormModal>
  );
};

export default POSSubCategoryFilterModal;
